import { REVENUE_OFFICIAL_VERSION, type RevenueDefinition } from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkdownContent } from '../../MarkdownContent.js';

/**
 * 版の一覧に並べる7項目。labelKey は描画側で t() する翻訳キー。
 * @graph-connects none
 */
const VERSION_COLUMNS: readonly {
  labelKey: string;
  value: (d: RevenueDefinition, t: (key: string) => string) => string;
}[] = [
  { labelKey: 'revenue.definitions.version', value: (d) => d.version },
  { labelKey: 'revenue.definitions.decidedOn', value: (d) => d.decidedOn ?? '' },
  { labelKey: 'revenue.definitions.decidedBy', value: (d) => d.decidedBy ?? '' },
  { labelKey: 'revenue.definitions.approver', value: (d) => d.approver ?? '' },
  { labelKey: 'revenue.definitions.changes', value: (d) => d.changes ?? '' },
  { labelKey: 'revenue.definitions.effectiveFrom', value: (d) => d.effectiveFrom ?? '' },
  {
    labelKey: 'revenue.definitions.retroactive',
    value: (d, t) =>
      d.retroactive === null
        ? ''
        : t(
            d.retroactive
              ? 'revenue.definitions.retroactiveYes'
              : 'revenue.definitions.retroactiveNo',
          ),
  },
];

/** @graph-connects none */
const TH =
  'border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]';
/** @graph-connects none */
const TD =
  'border-b border-[var(--color-border)] px-3 py-1.5 align-top text-sm text-[var(--color-text-primary)]';

/**
 * 版を選んでいないときに本文を出す版。正式版が一覧にあれば正式版、無ければ一覧の先頭（決定日が最新の版）。
 * 決定日が最新の版を既定にすると、正式版より新しい版を登録した時点で、画面の数字と違う版の本文が出るため。
 *
 * @param definitions 定義の版（決定日の新しい順）
 * @returns 既定で出す版（一覧が空なら undefined）
 * @graph-connects none
 */
function defaultDefinitionOf(
  definitions: readonly RevenueDefinition[],
): RevenueDefinition | undefined {
  return definitions.find((d) => d.version === REVENUE_OFFICIAL_VERSION) ?? definitions[0];
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（正式版）の定義タブ。定義の版の一覧（版番号・決定日・決定の場・承認者・変更点・適用開始月・遡及）と、選んだ版の正本の本文を表示する。版を選ぶまでは正式版の本文を出し、正式版が一覧に無ければ決定日が最新の版を出す。
 * @graph-connects MarkdownContent [calls] 正本の本文の表示
 * @graph-connects defaultDefinitionOf [calls] 版を選んでいないときに出す版
 */
export function LedgerDefinitionView({
  definitions,
}: {
  definitions: readonly RevenueDefinition[];
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  // 選んでいなければ既定の版を出す。選んだ版が取り直しで一覧から消えたときも既定に戻す。
  // 状態に確定させず導出にして、読み込み後に追従させる
  const current =
    definitions.find((d) => d.version === selected) ?? defaultDefinitionOf(definitions);

  if (current === undefined) {
    return (
      <Card className="mt-6 p-6">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('revenue.definitions.empty')}
        </p>
      </Card>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <div>
        <h2 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          {t('revenue.definitions.versionList')}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-base)]">
                {VERSION_COLUMNS.map((c) => (
                  <th key={c.labelKey} className={TH}>
                    {t(c.labelKey)}
                  </th>
                ))}
                <th className={TH}>{t('revenue.definitions.fingerprint')}</th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((d) => (
                <tr key={d.version}>
                  {VERSION_COLUMNS.map((c) => (
                    <td key={c.labelKey} className={TD}>
                      {c.value(d, t)}
                    </td>
                  ))}
                  <td className={`${TD} font-mono text-xs text-[var(--color-text-muted)]`}>
                    {d.logicFingerprint ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <Select
          size="sm"
          label={t('revenue.definitions.selectVersion')}
          options={definitions.map((d) => ({ value: d.version, label: d.version }))}
          value={current.version}
          onChange={(e) => {
            setSelected(e.target.value);
          }}
        />
        <Card className="p-6">
          {current.contentMd === null || current.contentMd === '' ? (
            <p className="text-sm text-[var(--color-text-secondary)]">
              {t('revenue.definitions.noContent')}
            </p>
          ) : (
            <MarkdownContent markdown={current.contentMd} />
          )}
        </Card>
      </div>
    </div>
  );
}
